import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/entities';
import type { ColumnDef } from '@tanstack/react-table';
import { ENTITY_TYPE_LABELS, type EntityResponse } from '@vagusx/clients';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useEntities, useEntity, useEntityContacts, useDeleteEntity } from '@/hooks/use-entities';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DeleteButton } from '@/components/custom/delete-button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  SheetActions,
  SheetBody,
  SheetCancel,
  SheetLoading,
} from '@/components/custom/sheet-layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDiscardGuard } from '@/hooks/use-discard-guard';
import { EntityEditor, EntityEditorForm } from './EntityEditor';

const ENTITY_TYPES = [
  { value: '', label: 'All' },
  { value: 'referring_provider', label: 'Referring Providers' },
  { value: 'lab', label: 'Labs' },
  { value: 'pharmacy', label: 'Pharmacies' },
  { value: 'payer', label: 'Payers' },
  { value: 'facility', label: 'Facilities' },
  { value: 'other', label: 'Other' },
] as const;

const TYPE_LABELS: Record<string, string> = ENTITY_TYPE_LABELS;

const columns: ColumnDef<EntityResponse>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant="outline">{TYPE_LABELS[row.original.type] ?? row.original.type}</Badge>
    ),
  },
  {
    accessorKey: 'npi',
    header: 'NPI',
    cell: ({ row }) => row.original.npi || <span className="text-muted-foreground">&mdash;</span>,
  },
  {
    accessorKey: 'documentCount',
    header: ({ column }) => <SortableHeader column={column}>Documents</SortableHeader>,
    cell: ({ row }) => row.original.documentCount,
  },
];

export default function EntitiesPage(): React.ReactElement {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<'create' | 'edit' | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const typeFilter = searchParams.type ?? '';
  const selectedEntityId = searchParams.id ?? null;

  const { data, isLoading, error } = useEntities({
    type: typeFilter || undefined,
    limit: 100,
  });
  const { data: selectedEntity } = useEntity(selectedEntityId ?? undefined);
  const { data: contacts } = useEntityContacts(selectedEntityId ?? undefined);
  const deleteMutation = useDeleteEntity();
  const discard = useDiscardGuard();

  const entities: EntityResponse[] = data?.items ?? [];

  const handleEntitySelect = (entity: EntityResponse): void => {
    navigate({
      to: '/setup/entities',
      search: { id: entity.id, type: typeFilter || undefined },
      replace: true,
    });
  };

  const handleCloseSheet = (): void => {
    setEditMode(null);
    navigate({ to: '/setup/entities', search: { type: typeFilter || undefined }, replace: true });
  };

  const handleTypeFilter = (value: string): void => {
    navigate({
      to: '/setup/entities',
      search: { type: value || undefined },
      replace: true,
    });
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteId) return;
    await deleteMutation.mutateAsync(deleteId);
    toast.success('Entity deleted');
    setDeleteId(null);
    handleCloseSheet();
  };

  return (
    <TablePage
      title="Entities"
      description="External providers, labs, pharmacies, and payers"
      columns={columns}
      data={entities}
      total={data?.total || undefined}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search entities..."
      onRowClick={handleEntitySelect}
      emptyMessage="No entities found."
      errorMessage="Failed to load entities."
      headerAction={
        <Button
          size="sm"
          onClick={() => {
            setEditMode('create');
            navigate({
              to: '/setup/entities',
              search: { type: typeFilter || undefined },
              replace: true,
            });
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Entity
        </Button>
      }
    >
      {/* Type filter tabs */}
      <div className="px-4 pb-2">
        <Tabs value={typeFilter} onValueChange={handleTypeFilter}>
          <TabsList>
            {ENTITY_TYPES.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Entity Detail Sheet — view and edit share it, so Edit swaps the content
          instead of sliding a second sheet over this one. */}
      <Sheet
        open={!!selectedEntityId && editMode !== 'create'}
        onOpenChange={(open) => {
          if (open) return;
          if (editMode === 'edit') discard.guard(handleCloseSheet);
          else handleCloseSheet();
        }}
      >
        <SheetContent className="w-full max-w-lg sm:max-w-xl">
          {editMode === 'edit' ? (
            selectedEntity ? (
              <EntityEditorForm
                entity={selectedEntity}
                onClose={() => discard.guard(() => setEditMode(null))}
                onSuccess={handleCloseSheet}
                onDirtyChange={discard.setDirty}
              />
            ) : (
              <SheetLoading title="Edit Entity" className="px-6" />
            )
          ) : (
            <>
              <SheetHeader className="px-6">
                <SheetTitle>{selectedEntity?.name}</SheetTitle>
                <SheetDescription>
                  {TYPE_LABELS[selectedEntity?.type ?? ''] ?? selectedEntity?.type}
                </SheetDescription>
              </SheetHeader>

              {selectedEntity && (
                <SheetBody className="px-6 pb-6 space-y-6">
                  <DetailSection title="Contact Information">
                    <DetailRow label="Phone" value={selectedEntity.phone} />
                    <DetailRow label="Fax" value={selectedEntity.fax} />
                    <DetailRow label="Email" value={selectedEntity.email} />
                  </DetailSection>

                  {(selectedEntity.addressLine1 || selectedEntity.city) && (
                    <DetailSection title="Address">
                      <div className="text-sm">
                        {selectedEntity.addressLine1 && <div>{selectedEntity.addressLine1}</div>}
                        {selectedEntity.addressLine2 && <div>{selectedEntity.addressLine2}</div>}
                        {(selectedEntity.city || selectedEntity.state) && (
                          <div>
                            {selectedEntity.city}
                            {selectedEntity.state ? `, ${selectedEntity.state}` : ''}{' '}
                            {selectedEntity.postalCode}
                          </div>
                        )}
                      </div>
                    </DetailSection>
                  )}

                  <DetailSection title="Details">
                    <DetailRow label="NPI" value={selectedEntity.npi} />
                    <DetailRow label="Documents" value={String(selectedEntity.documentCount)} />
                    {selectedEntity.metadata &&
                      Object.entries(selectedEntity.metadata).map(([key, value]) => (
                        <DetailRow key={key} label={key} value={String(value ?? '')} />
                      ))}
                  </DetailSection>

                  {contacts && contacts.length > 0 && (
                    <DetailSection title="Contacts">
                      <div className="space-y-3">
                        {contacts.map((contact) => (
                          <div key={contact.id} className="rounded-lg border p-3 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">
                                {contact.contactName ?? 'Unnamed'}
                              </span>
                              {contact.isPrimary && (
                                <Badge variant="default" className="text-xs">
                                  Primary
                                </Badge>
                              )}
                            </div>
                            {contact.role && (
                              <div className="text-xs text-muted-foreground">{contact.role}</div>
                            )}
                            {contact.phone && (
                              <div className="text-xs">
                                {contact.phone}
                                {contact.extension ? ` x${contact.extension}` : ''}
                              </div>
                            )}
                            {contact.email && <div className="text-xs">{contact.email}</div>}
                            {contact.notes && (
                              <div className="text-xs text-muted-foreground">{contact.notes}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </DetailSection>
                  )}
                </SheetBody>
              )}

              {selectedEntity && (
                <SheetActions
                  className="px-6"
                  start={<DeleteButton onClick={() => setDeleteId(selectedEntity.id)} />}
                >
                  <SheetCancel />
                  <Button onClick={() => setEditMode('edit')}>Edit</Button>
                </SheetActions>
              )}
            </>
          )}
        </SheetContent>
        {discard.dialog}
      </Sheet>

      {/* Create Sheet */}
      <EntityEditor
        open={editMode === 'create'}
        entity={null}
        onClose={() => setEditMode(null)}
        onSuccess={handleCloseSheet}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this entity? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TablePage>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
